import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  ChevronDown,
  CircleAlert,
  CloudUpload,
  FileAudio,
  FileText,
  FileVideo,
  X,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAppOriginFilterOptions } from '../../services/appOriginService';
import { DEMAND_TYPE_FILTER_OPTIONS } from '../../services/demandTypeService';
import { uploadTicketVideo, uploadTicketAudio } from '../../services/ticketService';
import ResponsibleMultiSelect from '../shared/ResponsibleMultiSelect';
import CompanyNameCombobox from '../shared/CompanyNameCombobox';
import { formatTicketProtocol } from '../../utils/ticketDisplayUtils';

const MAX_MEDIA_BYTES = 50 * 1024 * 1024;
const MAX_SEGMENTED_OPTIONS = 4;
const ACCEPTED_FILES =
  'image/*,video/*,audio/*,.pdf,.doc,.docx,.mp4,.webm,.mov,.mp3,.wav,.ogg,.m4a,.aac';

const isVideoFile = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith('video/')) return true;
  if (file.type && file.type.startsWith('audio/')) return false;
  const name = String(file.name || '').toLowerCase();
  return ['.mp4', '.webm', '.mov', '.m4v'].some((ext) => name.endsWith(ext));
};

const isAudioFile = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith('audio/')) return true;
  if (file.type && file.type.startsWith('video/')) return false;
  const name = String(file.name || '').toLowerCase();
  return ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac'].some((ext) => name.endsWith(ext));
};

const convertToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = (err) => reject(err);
  });

const getFileKey = (file) => `${file?.name || ''}-${file?.size || 0}-${file?.lastModified || 0}`;

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

const getFileKind = (file) => {
  if (file?.type?.startsWith('image/')) return 'Imagem';
  if (isVideoFile(file)) return 'Vídeo';
  if (isAudioFile(file)) return 'Áudio';
  return 'Documento';
};

const inputBase = css`
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-border-secondary);
  }

  &:focus {
    outline: none;
    border-color: var(--color-text-primary);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${({ $invalid }) =>
    $invalid &&
    css`
      border-color: rgba(220, 53, 69, 0.6);

      &:focus {
        border-color: #dc3545;
        box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
      }
    `}
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  & + & {
    padding-top: 1.5rem;
    border-top: 1px solid var(--color-border-primary);
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--color-text-muted);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;

  ${({ $full }) =>
    $full &&
    css`
      grid-column: 1 / -1;
    `}
`;

const Label = styled.label`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-primary);
`;

const LabelText = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-primary);
`;

const Optional = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--color-text-muted);
`;

const Input = styled.input`
  ${inputBase}
  height: 40px;
`;

const Textarea = styled.textarea`
  ${inputBase}
  min-height: 140px;
  resize: vertical;
`;

const SelectWrap = styled.div`
  position: relative;

  svg {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    pointer-events: none;
  }
`;

const Select = styled.select`
  ${inputBase}
  height: 40px;
  padding-right: 2.25rem;
  appearance: none;
  cursor: pointer;

  option {
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
  }
`;

const Helper = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--color-text-muted);
`;

const FieldError = styled.p`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
  font-size: 0.75rem;
  color: #dc3545;
`;

const Segmented = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 2px;
  width: fit-content;
  max-width: 100%;
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
`;

const SegmentedOption = styled.button`
  height: 32px;
  padding: 0 0.875rem;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? 'var(--color-bg-primary)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)')};
  box-shadow: ${({ $active }) => ($active ? 'var(--shadow-sm)' : 'none')};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const ResponsibleField = styled.div`
  min-width: 0;

  .responsible-container {
    gap: 0.375rem;
  }

  .responsible-container > .label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--color-text-primary);
  }

  #create-ticket-responsible {
    width: 100%;
    min-width: 0;
    height: 40px;
    border-radius: 8px;
    box-shadow: none;
    font-size: 0.875rem;
    font-weight: 400;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  #create-ticket-responsible:hover:not(:disabled) {
    border-color: var(--color-border-secondary);
  }

  #create-ticket-responsible:focus {
    border-color: var(--color-text-primary);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const HiddenFileInput = styled.input.attrs({ type: 'file' })`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
`;

const Dropzone = styled.label`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 1.25rem 1rem;
  border: 1px dashed var(--color-border-secondary);
  border-radius: 10px;
  background: var(--color-bg-secondary);
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

  svg {
    color: var(--color-text-muted);
    transition: transform 0.15s ease, color 0.15s ease;
  }

  &:hover {
    border-color: var(--color-border-accent);
  }

  &:focus-within {
    border-color: var(--color-text-primary);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  ${({ $active }) =>
    $active &&
    css`
      border-style: solid;
      border-color: var(--color-text-primary);
      background: var(--color-hover-overlay);

      svg {
        color: var(--color-text-primary);
        transform: translateY(-2px);
      }
    `}
`;

const DropzoneTitle = styled.span`
  font-size: 0.875rem;
  color: var(--color-text-primary);

  strong {
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const AttachmentList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  overflow: hidden;
`;

const AttachmentRow = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  background: var(--color-bg-primary);

  & + & {
    border-top: 1px solid var(--color-border-primary);
  }
`;

const AttachmentThumb = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 6px;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const AttachmentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  flex: 1;
`;

const AttachmentName = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AttachmentMeta = styled.span`
  font-size: 0.75rem;
  color: ${({ $warn }) => ($warn ? '#dc3545' : 'var(--color-text-muted)')};
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--color-hover-overlay);
    color: var(--color-text-primary);
  }
`;

const FormError = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 0.875rem;
  background: rgba(220, 53, 69, 0.08);
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: 8px;
  color: #dc3545;
  font-size: 0.8125rem;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const AttachmentKindIcon = ({ file }) => {
  if (isVideoFile(file)) return <FileVideo size={18} strokeWidth={1.75} aria-hidden />;
  if (isAudioFile(file)) return <FileAudio size={18} strokeWidth={1.75} aria-hidden />;
  return <FileText size={18} strokeWidth={1.75} aria-hidden />;
};

const buildEmptyForm = (demandType) => ({
  subjectDescription: '',
  detailedDescription: '',
  appOrigin: '',
  company: '',
  demandType: demandType || 'SUPPORT',
  attachments: [],
});

const CreateTicketForm = ({
  formId,
  onSuccess,
  onDirtyChange,
  onSubmittingChange,
  defaultDemandType = 'SUPPORT',
}) => {
  const { createTicket, fetchTickets, addToast, appOrigins, users } = useApp();
  const appOriginOptions = useMemo(() => getAppOriginFilterOptions(), [appOrigins]);
  const demandTypeOptions = DEMAND_TYPE_FILTER_OPTIONS;
  const [selectedResponsibleUserIds, setSelectedResponsibleUserIds] = useState([]);
  const [formData, setFormData] = useState(() => buildEmptyForm(defaultDemandType));
  const formDataRef = useRef(formData);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const attachmentObjectUrlsRef = useRef(new Map());
  const subjectRef = useRef(null);
  const descriptionRef = useRef(null);
  const submitErrorRef = useRef(null);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (submitError) {
      submitErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [submitError]);

  const dirty = useMemo(
    () =>
      Boolean(
        formData.subjectDescription.trim() ||
          formData.detailedDescription.trim() ||
          formData.appOrigin ||
          formData.company.trim() ||
          formData.attachments.length > 0 ||
          selectedResponsibleUserIds.length > 0 ||
          formData.demandType !== (defaultDemandType || 'SUPPORT')
      ),
    [formData, selectedResponsibleUserIds, defaultDemandType]
  );

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    onSubmittingChange?.(submitting);
  }, [submitting, onSubmittingChange]);

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSubmitError(null);
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleChange = (e) => updateField(e.target.name, e.target.value);

  const attachmentPreviews = useMemo(() => {
    const files = formData.attachments || [];
    return files.map((file) => {
      const fileKey = getFileKey(file);
      const isImage = Boolean(file?.type?.startsWith('image/'));
      let url = null;

      if (isImage) {
        const existing = attachmentObjectUrlsRef.current.get(fileKey);
        if (existing) {
          url = existing;
        } else {
          url = URL.createObjectURL(file);
          attachmentObjectUrlsRef.current.set(fileKey, url);
        }
      }

      return { file, fileKey, isImage, url };
    });
  }, [formData.attachments]);

  useEffect(() => {
    const currentKeys = new Set((formData.attachments || []).map(getFileKey));
    for (const [key, url] of attachmentObjectUrlsRef.current.entries()) {
      if (!currentKeys.has(key)) {
        URL.revokeObjectURL(url);
        attachmentObjectUrlsRef.current.delete(key);
      }
    }
  }, [formData.attachments]);

  useEffect(() => {
    const urls = attachmentObjectUrlsRef.current;
    return () => {
      for (const url of urls.values()) {
        URL.revokeObjectURL(url);
      }
      urls.clear();
    };
  }, []);

  const addFiles = (newFiles) => {
    if (!newFiles.length) return;
    setFormData((prev) => {
      const merged = [...(prev.attachments || []), ...newFiles];
      const seen = new Set();
      const deduped = merged.filter((file) => {
        const key = getFileKey(file);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const next = { ...prev, attachments: deduped };
      formDataRef.current = next;
      return next;
    });
    setSubmitError(null);
  };

  const handleFileChange = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const removeAttachmentByKey = (keyToRemove) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        attachments: (prev.attachments || []).filter((file) => getFileKey(file) !== keyToRemove),
      };
      formDataRef.current = next;
      return next;
    });
    setSubmitError(null);
  };

  const handleDragOver = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragActive) setDragActive(true);
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer?.files || []));
  };

  const handleDemandTypeKeyDown = (e, index) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const step = e.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (index + step + demandTypeOptions.length) % demandTypeOptions.length;
    updateField('demandType', demandTypeOptions[nextIndex].value);
    const group = e.currentTarget.parentElement;
    group?.children?.[nextIndex]?.focus();
  };

  const validate = () => {
    const current = formDataRef.current;
    const errors = {};
    if (!current.subjectDescription.trim()) {
      errors.subjectDescription = 'Informe um assunto para o ticket.';
    }
    if (!current.detailedDescription.trim()) {
      errors.detailedDescription = 'Descreva o problema ou a solicitação.';
    }
    setFieldErrors(errors);
    if (errors.subjectDescription) {
      subjectRef.current?.focus();
    } else if (errors.detailedDescription) {
      descriptionRef.current?.focus();
    }
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError(null);
    if (!validate()) return;
    setSubmitting(true);

    try {
      const currentFormData = formDataRef.current;
      const ticketData = {
        subjectDescription: currentFormData.subjectDescription.trim(),
        detailedDescription: currentFormData.detailedDescription.trim(),
        appOrigin: currentFormData.appOrigin || undefined,
        companyName: currentFormData.company?.trim() || undefined,
        demandType: currentFormData.demandType || 'SUPPORT',
      };

      if (selectedResponsibleUserIds.length > 0) {
        const idSet = new Set(selectedResponsibleUserIds.map(String));
        const responsibleUsers = (users || []).filter((u) => idSet.has(String(u?.id)));
        if (responsibleUsers.length > 0) {
          ticketData.responsibleUsers = responsibleUsers;
        }
      }

      if (currentFormData.attachments?.length > 0) {
        const otherFiles = currentFormData.attachments.filter(
          (file) => !isVideoFile(file) && !isAudioFile(file)
        );
        if (otherFiles.length > 0) {
          ticketData.attachments = await Promise.all(otherFiles.map((file) => convertToBase64(file)));
        }
      }

      const created = await createTicket(ticketData);
      const createdId = created?.id;

      const videoFiles = (currentFormData.attachments || []).filter(isVideoFile);
      const acceptedVideos = videoFiles.filter((file) => file.size <= MAX_MEDIA_BYTES);
      const rejectedVideos = videoFiles.filter((file) => file.size > MAX_MEDIA_BYTES);

      if (rejectedVideos.length > 0) {
        addToast('Alguns vídeos passaram de 50 MB e não foram enviados.', 'error');
      }

      if (createdId && acceptedVideos.length > 0) {
        try {
          await Promise.all(acceptedVideos.map((file) => uploadTicketVideo(createdId, file)));
          addToast(
            acceptedVideos.length === 1
              ? 'Vídeo anexado ao ticket.'
              : `${acceptedVideos.length} vídeos anexados ao ticket.`,
            'success'
          );
        } catch (videoErr) {
          console.error('Error uploading ticket videos:', videoErr);
          addToast(
            videoErr?.response?.data?.message ||
              'Ticket criado, mas falhou o envio de um ou mais vídeos.',
            'error'
          );
        }
      }

      const audioFiles = (currentFormData.attachments || []).filter(isAudioFile);
      const acceptedAudios = audioFiles.filter((file) => file.size <= MAX_MEDIA_BYTES);
      const rejectedAudios = audioFiles.filter((file) => file.size > MAX_MEDIA_BYTES);

      if (rejectedAudios.length > 0) {
        addToast('Alguns áudios passaram de 50 MB e não foram enviados.', 'error');
      }

      if (createdId && acceptedAudios.length > 0) {
        try {
          await Promise.all(acceptedAudios.map((file) => uploadTicketAudio(createdId, file)));
          addToast(
            acceptedAudios.length === 1
              ? 'Áudio anexado ao ticket.'
              : `${acceptedAudios.length} áudios anexados ao ticket.`,
            'success'
          );
        } catch (audioErr) {
          console.error('Error uploading ticket audios:', audioErr);
          addToast(
            audioErr?.response?.data?.message ||
              'Ticket criado, mas falhou o envio de um ou mais áudios.',
            'error'
          );
        }
      }

      const protocol = formatTicketProtocol(created);
      addToast(
        protocol ? `Ticket criado · Protocolo ${protocol}` : 'Ticket criado com sucesso!',
        'success',
        createdId ? { action: { label: 'Ver ticket', to: `/user/tickets/${createdId}` } } : undefined
      );

      const cleared = buildEmptyForm(defaultDemandType);
      formDataRef.current = cleared;
      setFormData(cleared);
      setSelectedResponsibleUserIds([]);
      setFieldErrors({});
      setSubmitting(false);
      fetchTickets();
      onSuccess?.(created);
    } catch (err) {
      console.error('Error creating ticket:', err);
      const message =
        err?.response?.data?.message || err?.message || 'Erro ao criar ticket. Tente novamente.';
      setSubmitError(message);
      setSubmitting(false);
    }
  };

  const useSegmented =
    demandTypeOptions.length > 1 && demandTypeOptions.length <= MAX_SEGMENTED_OPTIONS;

  return (
    <Form id={formId} onSubmit={handleSubmit} noValidate>
      <Section aria-labelledby={`${formId}-section-main`}>
        <SectionTitle id={`${formId}-section-main`}>O que aconteceu</SectionTitle>

        <Field>
          <Label htmlFor="subjectDescription">Assunto</Label>
          <Input
            ref={subjectRef}
            type="text"
            id="subjectDescription"
            name="subjectDescription"
            value={formData.subjectDescription}
            onChange={handleChange}
            placeholder="Ex.: Erro ao gerar boleto no CRM"
            maxLength={200}
            autoComplete="off"
            autoFocus
            aria-required="true"
            aria-invalid={Boolean(fieldErrors.subjectDescription)}
            aria-describedby={
              fieldErrors.subjectDescription ? 'subjectDescription-error' : 'subjectDescription-help'
            }
            $invalid={Boolean(fieldErrors.subjectDescription)}
          />
          {fieldErrors.subjectDescription ? (
            <FieldError id="subjectDescription-error">
              <CircleAlert size={13} strokeWidth={2} aria-hidden />
              {fieldErrors.subjectDescription}
            </FieldError>
          ) : (
            <Helper id="subjectDescription-help">Um resumo curto que identifique o problema.</Helper>
          )}
        </Field>

        <Field>
          <Label htmlFor="detailedDescription">Descrição</Label>
          <Textarea
            ref={descriptionRef}
            id="detailedDescription"
            name="detailedDescription"
            value={formData.detailedDescription}
            onChange={handleChange}
            placeholder="O que você estava fazendo, o que esperava que acontecesse e o que aconteceu."
            aria-required="true"
            aria-invalid={Boolean(fieldErrors.detailedDescription)}
            aria-describedby={
              fieldErrors.detailedDescription ? 'detailedDescription-error' : 'detailedDescription-help'
            }
            $invalid={Boolean(fieldErrors.detailedDescription)}
          />
          {fieldErrors.detailedDescription ? (
            <FieldError id="detailedDescription-error">
              <CircleAlert size={13} strokeWidth={2} aria-hidden />
              {fieldErrors.detailedDescription}
            </FieldError>
          ) : (
            <Helper id="detailedDescription-help">
              Quanto mais contexto, mais rápido o suporte consegue resolver.
            </Helper>
          )}
        </Field>
      </Section>

      <Section aria-labelledby={`${formId}-section-details`}>
        <SectionTitle id={`${formId}-section-details`}>Detalhes</SectionTitle>

        <Grid>
          <Field $full>
            {useSegmented ? (
              <>
                <LabelText id={`${formId}-demand-label`}>Tipo de demanda</LabelText>
                <Segmented role="radiogroup" aria-labelledby={`${formId}-demand-label`}>
                  {demandTypeOptions.map((option, index) => {
                    const active = formData.demandType === option.value;
                    return (
                      <SegmentedOption
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        tabIndex={active ? 0 : -1}
                        $active={active}
                        onClick={() => updateField('demandType', option.value)}
                        onKeyDown={(e) => handleDemandTypeKeyDown(e, index)}
                      >
                        {option.label}
                      </SegmentedOption>
                    );
                  })}
                </Segmented>
              </>
            ) : (
              <>
                <Label htmlFor="demandType">Tipo de demanda</Label>
                <SelectWrap>
                  <Select
                    id="demandType"
                    name="demandType"
                    value={formData.demandType}
                    onChange={handleChange}
                  >
                    {demandTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <ChevronDown size={16} strokeWidth={2} aria-hidden />
                </SelectWrap>
              </>
            )}
          </Field>

          <Field>
            <Label htmlFor="appOrigin">
              Aplicação
              <Optional>Opcional</Optional>
            </Label>
            <SelectWrap>
              <Select id="appOrigin" name="appOrigin" value={formData.appOrigin} onChange={handleChange}>
                <option value="">Selecione a aplicação</option>
                {appOriginOptions.map((origin) => (
                  <option key={origin.value} value={origin.value}>
                    {origin.label}
                  </option>
                ))}
              </Select>
              <ChevronDown size={16} strokeWidth={2} aria-hidden />
            </SelectWrap>
          </Field>

          <Field>
            <Label htmlFor="company">
              Empresa
              <Optional>Opcional</Optional>
            </Label>
            <CompanyNameCombobox
              id="company"
              value={formData.company}
              onChange={(name) => updateField('company', name)}
              placeholder="Digite ou selecione"
            />
          </Field>

          <Field $full>
            <ResponsibleField>
              <ResponsibleMultiSelect
                users={users}
                selectedIds={selectedResponsibleUserIds}
                onSelectedIdsChange={setSelectedResponsibleUserIds}
                fieldId="create-ticket-responsible"
                label="Responsáveis"
                showUserIcon={false}
              />
            </ResponsibleField>
          </Field>
        </Grid>
      </Section>

      <Section aria-labelledby={`${formId}-section-files`}>
        <SectionTitle id={`${formId}-section-files`}>Anexos</SectionTitle>

        <Dropzone
          htmlFor={`${formId}-attachments`}
          $active={dragActive}
          onDragEnter={handleDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <HiddenFileInput
            id={`${formId}-attachments`}
            name="attachments"
            multiple
            onChange={handleFileChange}
            accept={ACCEPTED_FILES}
          />
          <CloudUpload size={22} strokeWidth={1.75} aria-hidden />
          <DropzoneTitle>
            {dragActive ? (
              'Solte para anexar'
            ) : (
              <>
                Arraste arquivos aqui ou <strong>selecione</strong>
              </>
            )}
          </DropzoneTitle>
          <Helper>Imagens, PDF, DOC, vídeos e áudios de até 50 MB.</Helper>
        </Dropzone>

        {attachmentPreviews.length > 0 && (
          <AttachmentList aria-label="Arquivos anexados">
            {attachmentPreviews.map((p) => {
              const tooLarge =
                (isVideoFile(p.file) || isAudioFile(p.file)) && p.file.size > MAX_MEDIA_BYTES;
              return (
                <AttachmentRow key={p.fileKey}>
                  <AttachmentThumb>
                    {p.isImage ? <img src={p.url} alt="" /> : <AttachmentKindIcon file={p.file} />}
                  </AttachmentThumb>
                  <AttachmentInfo>
                    <AttachmentName title={p.file?.name || ''}>
                      {p.file?.name || 'Arquivo anexado'}
                    </AttachmentName>
                    <AttachmentMeta $warn={tooLarge}>
                      {getFileKind(p.file)}
                      {formatFileSize(p.file?.size) ? ` · ${formatFileSize(p.file.size)}` : ''}
                      {tooLarge ? ' · acima de 50 MB, não será enviado' : ''}
                    </AttachmentMeta>
                  </AttachmentInfo>
                  <RemoveButton
                    type="button"
                    onClick={() => removeAttachmentByKey(p.fileKey)}
                    aria-label={`Remover ${p.file?.name || 'anexo'}`}
                    title="Remover"
                  >
                    <X size={16} strokeWidth={2} aria-hidden />
                  </RemoveButton>
                </AttachmentRow>
              );
            })}
          </AttachmentList>
        )}
      </Section>

      {submitError && (
        <FormError ref={submitErrorRef} role="alert">
          <CircleAlert size={16} strokeWidth={2} aria-hidden />
          <span>{submitError}</span>
        </FormError>
      )}
    </Form>
  );
};

export default CreateTicketForm;
